import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  ClickAwayListener,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableCell as BaseTableCell,
  TableRow as BaseTableRow,
  tableCellClasses,
  styled,
  TextField,
  TableSortLabel,
} from '@mui/material';
import _ from 'lodash';
import {
  PersonAdd as AddAccountIcon,
  FilterAlt as FilterIcon,
  Help as HelpIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

import { fetchMembers } from 'redux/actions';

const STATUS = {
  a: 'Active',
  t: 'Temp',
  x: 'Inactive',
};

const mockData = [
  {
    id: 1,
    name: 'Jon Doe',
    organization: 'Org1',
    email: 'jondoe@gmail.com',
    status: 'Re-send Invitation',
  },
  {
    id: 2,
    name: 'Adam Hockman',
    organization: 'Org3',
    email: 'adamhockman12@gmail.com',
    status: 'Accepted',
  },
  {
    id: 3,
    name: 'Adam Skarr',
    organization: 'Org2',
    email: 'askarr@morningsideacademy.org',
    status: 'Declined',
  },
  {
    id: 4,
    name: 'Aiman Fatima',
    organization: 'Org5',
    email: 'aimanhussain16@gmail.com',
    status: 'Accepted',
  },
  {
    id: 5,
    name: 'Alaina Peduzzi',
    organization: 'Org1',
    email: 'A.N.Peduzzi1@eagle.clarion.edu',
    status: 'Revoked',
  },
  {
    id: 6,
    name: 'Aleena Ali',
    organization: 'Org3',
    email: 'aleenaali44@gmail.com',
    status: 'Accepted',
  },
  {
    id: 7,
    name: 'Alexis Hubman',
    organization: 'Org2',
    email: 'ahhubman@gmail.com',
    status: 'Accepted',
  },
];

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    paddingBottom: theme.spacing(3),
    backgroundColor: '#fff',
    borderRadius: '4px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f9fafc',
    padding: theme.spacing(2),
    borderBottom: 'solid 1px #eaeef1',
  },
  subHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  description: {
    display: 'flex',
    alignItems: 'center',
    color: '#586065',
    fontSize: '0.75rem',
    backgroundColor: '#f2f4f8',
    borderRadius: '4px',
    '& svg': {
      fontSize: '14px',
    },
  },
  button: {
    marginLeft: 'auto',
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    backgroundColor: '#bf9d30',
    fontSize: '0.75rem',
    color: '#fff',
    borderRadius: '16px',
    '&:hover': {
      backgroundColor: '#bf9d30',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
    },
    '& svg': {
      fontSize: '14px',
    },
  },
  tooltipButton: {
    marginLeft: 'auto',
    '& svg': {
      fontSize: '16px',
    },
  },
  tableContainer: {
    padding: theme.spacing(0, 2),
    width: 'unset',
    '& table': {
      width: '100%',
    },
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      fontSize: '12px',
    },
  },
  nameCellBadge: {
    marginLeft: 'auto',
    padding: theme.spacing(0.5, 1),
    backgroundColor: '#337ab7',
    color: '#fff',
    fontSize: '0.5rem',
    borderRadius: '4px',
  },
  resendButton: {
    color: '#000',
    fontSize: '0.6rem',
    textTransform: 'none',
  },
  email: {
    textDecoration: 'none',
  },
}));

const TableCell = styled(BaseTableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f9fafc',
    borderTop: 'solid 1px #eaeef1',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const TableRow = styled(BaseTableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:nth-of-type(even)': {
    backgroundColor: '#f9fafc',
  },
  // hide last border
  '& td, & th': {
    padding: theme.spacing(1.5),
    border: 'solid 2px #fff',
  },
}));

const UserList = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const nameInputRef = useRef();
  const orgInputRef = useRef();
  const members = useSelector((state) => state?.members);
  const [users, setUsers] = useState(mockData);
  const [showTooltip, setShowTooltip] = useState(false);
  const [nameOrEmailKeyword, setNameOrEmailKeyword] = useState('');
  const [organizationKeyword, setOrganizationKeyword] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState();

  const handleTooltipClose = () => {
    setShowTooltip(false);
  };

  const handleTooltipOpen = () => {
    setShowTooltip(true);
  };

  const handleResend = () => {
    // TODO
  };

  const onNameOrEmailSearchChange = (event) => {
    const keyword = event.target.value;

    setNameOrEmailKeyword(keyword);
  };

  const onOrganizationSearchChange = (event) => {
    const keyword = event.target.value;

    setOrganizationKeyword(keyword);
  };

  const sortHandler = (column) => {
    const filtered = users.sort((a, b) => {
      if (b[column].toLowerCase() < a[column].toLowerCase()) {
        return -1;
      }

      if (b[column].toLowerCase() > a[column].toLowerCase()) {
        return 1;
      }

      return 0;
    });

    if (order === 'desc') {
      filtered.reverse();
    }

    setUsers(filtered);
    setOrderBy(column);
    setOrder(order === 'asc' ? 'desc' : 'asc');
  };

  useEffect(() => {
    dispatch(fetchMembers());
  }, [dispatch]);

  useEffect(() => {
    const filtered = mockData.filter(
      (user) =>
        (user.name.toLowerCase().includes(nameOrEmailKeyword.toLowerCase()) ||
          user.email
            .toLowerCase()
            .includes(nameOrEmailKeyword.toLowerCase())) &&
        user.organization
          .toLowerCase()
          .includes(organizationKeyword.toLowerCase())
    );

    setUsers(
      nameOrEmailKeyword === '' && organizationKeyword === ''
        ? mockData
        : filtered
    );
  }, [nameOrEmailKeyword, organizationKeyword]);

  return (
    <Paper className={classes.paper} elevation={1}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.headerTitle}>
            Member Accounts
          </Typography>
          <br />
          {/* <Typography variant='body2'>Test Organization</Typography>
          <Typography variant='body2'>Subscription: Active</Typography>
          <Typography variant='body2'>Expiration: 2023-10-20</Typography>
          <Typography variant='body2'>Members allocated: 25/100</Typography> */}
        </div>
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <Tooltip
            PopperProps={{
              disablePortal: true,
            }}
            onClose={handleTooltipClose}
            open={showTooltip}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            arrow
            placement='left'
            title="This is a list of the users in this system. To view a user's information, click on their name. To add a new user, click the New User button. You can change the way the list is sorted using the Show menu."
          >
            <IconButton
              className={classes.tooltipButton}
              onClick={showTooltip ? handleTooltipClose : handleTooltipOpen}
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </ClickAwayListener>
      </div>
      <div className={classes.subHeader}>
        <Typography component='div' className={classes.description}>
          <FilterIcon sx={{ marginRight: '4px' }} />
          Showing all records sorted by name
        </Typography>
        <TextField
          inputRef={nameInputRef}
          sx={{ ml: 4, width: 230 }}
          placeholder='Search by Name or Email'
          inputProps={{
            'aria-label': 'search by name or email',
            style: { fontSize: 12 },
          }}
          size='small'
          onChange={_.debounce(onNameOrEmailSearchChange, 1000)}
          InputProps={{
            endAdornment: nameOrEmailKeyword ? (
              <IconButton
                type='button'
                aria-label='clear'
                onClick={() => {
                  nameInputRef.current.value = '';
                  setNameOrEmailKeyword('');
                }}
                sx={{ visibility: nameOrEmailKeyword ? 'visible' : 'hidden' }}
              >
                <CancelIcon />
              </IconButton>
            ) : (
              <SearchIcon />
            ),
          }}
        />
        <TextField
          inputRef={orgInputRef}
          sx={{ ml: 2, width: 230 }}
          placeholder='Search by Organization'
          inputProps={{
            'aria-label': 'search by organization',
            style: { fontSize: 12 },
          }}
          size='small'
          onChange={_.debounce(onOrganizationSearchChange, 1000)}
          InputProps={{
            endAdornment: organizationKeyword ? (
              <IconButton
                type='button'
                aria-label='clear'
                onClick={() => {
                  orgInputRef.current.value = '';
                  setOrganizationKeyword('');
                }}
                sx={{ visibility: organizationKeyword ? 'visible' : 'hidden' }}
              >
                <CancelIcon />
              </IconButton>
            ) : (
              <SearchIcon />
            ),
          }}
        />
        <Button
          className={classes.button}
          onClick={() => navigate('/admin/users/new')}
        >
          <AddAccountIcon sx={{ marginRight: '8px' }} />
          New Account
        </Button>
      </div>
      <TableContainer className={classes.tableContainer}>
        <Table sx={{ minWidth: 700 }} aria-label='customized table'>
          <TableHead>
            <TableRow>
              <TableCell align='center' sortDirection={order}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={order}
                  onClick={() => sortHandler('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell align='center'>Organization</TableCell>
              <TableCell align='center'>{`Email (click to contact)`}</TableCell>
              <TableCell align='center' sortDirection={order}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={order}
                  onClick={() => sortHandler('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((row) => (
              <TableRow
                key={row.user_id}
                hover
                onClick={() => navigate(`/admin/users/${row.user_id}`)}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={{
                    width: '50%',
                  }}
                >
                  <div className={classes.nameCell}>
                    <PersonIcon
                      sx={{
                        color: '#a6a8a7',
                        marginRight: '8px',
                      }}
                    />
                    <Typography sx={{ fontWeight: '600' }}>
                      {row.full_name}
                    </Typography>
                    <span className={classes.nameCellBadge}>ELS</span>
                  </div>
                </TableCell>
                <TableCell align='center'>
                  <span>{row.group || ''}</span>
                </TableCell>
                <TableCell align='center'>
                  <a className={classes.email} href={`mailto:${row.email}`}>
                    {row.email}
                  </a>
                </TableCell>
                <TableCell align='center'>
                  <Button
                    className={classes.resendButton}
                    onClick={handleResend}
                  >
                    {STATUS[row.status]}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default UserList;
